import { Button, Modal, Form, Container, Row, Col } from "react-bootstrap";
import { SyntheticEvent, useEffect, useState } from "react";
import { Problem, ValidationProblem, formErrors } from "../api/backendApi";
import FormErrorDisplay from "../common/FormErrorDisplay";
import { QrCodeModel, qrCodeAPI } from "../api/qrCodeAPI";
import ErrorDisplay from "../common/ErrorDisplay";
import { TrashFill } from "react-bootstrap-icons";

interface QrCodeModalProps {
  recId: number;
  onCancel: () => void;
  modalText: string;
  disabled: boolean;
}

export function QrCodeModal({
  recId,
  onCancel,
  modalText,
  disabled,
}: QrCodeModalProps) {
  const [modalTitle, setModalTitle] = useState(modalText);
  const [qrCode, setQrCode] = useState<QrCodeModel>({
    qrCode: "",
  });
  const [qrCodes, setQrCodes] = useState<QrCodeModel[]>([]);
  const [editQrCode, setEditQrCode] = useState<QrCodeModel | undefined>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Problem | undefined>(undefined);
  const [errors, setErrors] = useState<formErrors>({});
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    async function loadModal(recId: number) {
      loadQrCodes(recId);
    }
    loadModal(recId);
  }, [recId]);

  const loadQrCodes = async (recId: number) => {
    setLoading(true);
    const data = await qrCodeAPI.getQrCodes(recId);

    if (data.ok) {
      setError(undefined);
      setQrCodes(data.value);
    } else {
      setError(data.problem);
    }

    setLoading(false);
  };

  const deleteQrCode = async (recId: number, qrCode: QrCodeModel) => {
    setLoading(true);

    if (!qrCode) {
      setLoading(false);
      return;
    }

    const result = await qrCodeAPI.deleteQrCode(recId, qrCode);

    if (result.ok) {
      setErrors({});
      loadQrCodes(recId);
    } else {
      setLoading(false);
    }
  };

  const handleChange = (event: any) => {
    const { name, value } = event.target;

    const change = { [name]: value };
    const modified: QrCodeModel = { ...qrCode, ...change };

    setQrCode(modified);
  };

  const handleSubmit = (event: SyntheticEvent) => {
    event.preventDefault();

    let call =
      isEditing && editQrCode
        ? qrCodeAPI.updateQrCode(recId, editQrCode.qrCode ?? "", qrCode)
        : qrCodeAPI.createQrCode(recId, qrCode);
    call.then((result) => {
      if (result.ok) {
        setErrors({});
        loadQrCodes(recId);
        setIsEditing(false);
        setEditQrCode(undefined);
        setModalTitle(modalText);
        setQrCode({
          qrCode: "",
        });
      } else {
        let validation = result.problem as ValidationProblem;
        if (validation.validation) {
          setErrors(validation.validation);
        } else {
          let errors: formErrors = {};
          errors[""] = [`${result.problem.title} [${result.problem.instance}]`];
          setErrors(errors);
        }
      }
    });
  };

  return (
    <>
      <Modal onHide={() => onCancel()} show={true} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>{modalTitle}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Container>
            <Row>
              {!disabled && (
                <Col>
                  <Form onSubmit={handleSubmit} id="qrCodeForm">
                    <FormErrorDisplay errors={errors[""]} />
                    <Form.Label htmlFor="qrCode">QR Code</Form.Label>
                    <Form.Control
                      name="qrCode"
                      type="text"
                      id="qrCode"
                      placeholder={"QR Code..."}
                      value={qrCode.qrCode}
                      onChange={handleChange}
                    />
                    <FormErrorDisplay errors={errors.qrCode} />
                  </Form>
                  {isEditing && (
                    <>
                      <Button
                        variant="primary"
                        className="mt-3"
                        onClick={handleSubmit}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="secondary"
                        className="mt-3 ms-3"
                        onClick={() => {
                          setErrors({});
                          setIsEditing(false);
                          setEditQrCode(undefined);
                          setQrCode({
                            qrCode: "",
                          });
                          setModalTitle(modalText);
                        }}
                      >
                        Cancel
                      </Button>
                    </>
                  )}
                  {!isEditing && (
                    <Button
                      variant="primary"
                      form="qrCodeForm"
                      type="submit"
                      className="mt-3"
                    >
                      Add
                    </Button>
                  )}
                </Col>
              )}
              <Col>
                <div className="fixed-table-header">
                  <table className="table table-hover">
                    <thead>
                      <tr>
                        <th>QR Code</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading && (
                        <tr>
                          <td colSpan={2}>Loading...</td>
                        </tr>
                      )}

                      {error && (
                        <tr>
                          <td colSpan={2}>
                            <ErrorDisplay error={error}></ErrorDisplay>
                          </td>
                        </tr>
                      )}
                      {!loading &&
                        !error &&
                        (qrCodes.length === 0 ? (
                          <tr>
                            <td colSpan={2}>No QR Codes...</td>
                          </tr>
                        ) : (
                          qrCodes.map((x) => (
                            <tr
                              key={x.qrCode}
                              onClick={() => {
                                if (!disabled) {
                                  setErrors({});
                                  setEditQrCode(x);
                                  setModalTitle(`Edit QR Code ${x.qrCode}`);
                                  setIsEditing(true);
                                  setQrCode(x);
                                }
                              }}
                            >
                              <td>{x.qrCode}</td>
                              <td className="text-end">
                                <TrashFill
                                  className="text-danger"
                                  height={24}
                                  width={24}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (
                                      window.confirm(
                                        "Are you sure you want to delete this QR code?",
                                      )
                                    ) {
                                      deleteQrCode(recId, x);
                                    }
                                  }}
                                ></TrashFill>
                              </td>
                            </tr>
                          ))
                        ))}
                    </tbody>
                  </table>
                </div>
              </Col>
            </Row>
          </Container>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onCancel}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
